/**
 * Growth Audit recurring module access is independent of purchased audit reports.
 * Display only server-evaluated entitlements; never compute tier from URL or browser claims.
 */
import { Card, CardHead, Loading, Text } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { useFormat } from '../../../lib/format';
import { useResource } from '../../../lib/resource';

/**
 * The module card and the Resolve findings table stay hidden until recurring module billing exists (no Stripe checkout
 * or webhook fills billing_module_subscriptions yet). Same flag as the Resolve drawer slot; off in prod.
 */
export const GROWTH_AUDIT_MODULE_FLAG = 'audit.remediation';

export interface GrowthAuditModuleView {
  module: 'growth-audit';
  subscription: null | {
    tier: string;
    status: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
  };
  capabilities: { module: boolean; resolvePlan: boolean; resolveExecute: false };
  reportAccess: 'per-purchase-entitlement';
  reason: string | null;
}

export function GrowthAuditModuleCard() {
  const { t } = useApp();
  const f = useFormat();
  const status = useResource<GrowthAuditModuleView>('/v1/growth-audit/module');
  return (
    <Card aria-labelledby="growth-audit-module-subscription">
      <CardHead id="growth-audit-module-subscription" title={t('shell.auditModule.title')}
        sub={t('shell.auditModule.independentReports')} />
      {status.status === 'loading' ? <Loading label={t('common.loading')} /> :
        status.status === 'error' ? <Text role="alert">{t('shell.auditModule.unavailable')}</Text> :
        !status.data.subscription ? <Text>{t('shell.auditModule.notSubscribed')}</Text> : (
          <div className="qm-stack">
            <Text>
              <strong>{status.data.subscription.tier === 'enterprise' ? t('shell.auditModule.tier.enterprise') :
                status.data.subscription.tier === 'pro' ? t('shell.auditModule.tier.pro') :
                  t('shell.auditModule.tier.essential')}</strong>
              {' · '}
              {status.data.capabilities.module ? t('shell.auditModule.active') : t('shell.auditModule.inactive')}
              {' · '}{t('shell.auditModule.until')} {f.date(status.data.subscription.currentPeriodEnd)}
            </Text>
            <Text tone="muted">
              {status.data.capabilities.resolvePlan ? t('shell.auditModule.planAvailable') : t('shell.auditModule.planLocked')}
            </Text>
            <Text tone="muted">{t('shell.auditModule.executionConsent')}</Text>
          </div>
        )}
    </Card>
  );
}

